import { t } from "@lingui/macro";

import { i18n } from "../libs/i18n";
import {
  parseDate,
  formatDate,
  formatToLocale,
  dateFromString
} from "./date.util";
import { getTextFromMarkdown } from "../libs/utils/markdown";
import {
  emailRegEx,
  urlRegEx,
  alphabetRegEx,
  noSpecialCharactersRegEx,
  alphanumericRegEx,
  numberRegEx
} from "./validation.regex";
import { FIELD_TYPES } from "../constants";

const formatChecklist = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }
  value = value.filter((item) => {
    return item.selected;
  });
  value = value.map((item) => {
    return item.value;
  });
  return value;
};

const isBlank = (value) =>
  [null, "", undefined].includes(value) ||
  (Array.isArray(value) && value.isEmpty());

const isEmpty = (value) => (isBlank(value) ? undefined : "is not empty");

const isNotEmpty = (value) => (!isBlank(value) ? undefined : "is empty");

const getDisplayValue = (value, fieldName, type) => {
  let displayValue = fieldName || value;
  if (!fieldName && value && (type === "Date" || type === "DateTime")) {
    let includeTime = type === "DateTime";
    let dateString = formatDate(value, includeTime);
    displayValue = formatToLocale(dateString, includeTime);
  } else if (
    !fieldName &&
    value &&
    (type === "User" || type === "UserAndGroup")
  ) {
    displayValue = value.Name;
  }
  return displayValue;
};

export const required = (value, type) => {
  let isRequired = false;
  if (type === "Checklist") {
    if (Array.isArray(value)) {
      let data = value.filter((item) => {
        return item.selected;
      });
      isRequired = data.length >= 1 ? true : undefined;
    } else {
      isRequired = true;
    }
  } else if (Array.isArray(value)) {
    isRequired = value.length > 0;
  } else {
    isRequired = value || value === 0;
  }
  return isRequired ? undefined : i18n._(t`Value is required`);
};

export const maxLength = (value, max) =>
  value && value.length > parseInt(max, 10)
    ? i18n._(t`Value must be ${max} characters or less`)
    : undefined;

export const minLength = (value, min) =>
  value && value.length < parseInt(min, 10)
    ? i18n._(t`Value must be ${min} characters or more`)
    : undefined;

export const number = (value) =>
  value && isNaN(Number(value)) ? i18n._(t`Value must be a number`) : undefined;

//should match unicode character?
export const isAlphabet = (value) =>
  value && !alphabetRegEx.test(value)
    ? i18n._(t`Value must contain only alphabets`)
    : undefined;

export const noSpecialCharacters = (value) =>
  value && noSpecialCharactersRegEx.test(value)
    ? i18n._(t`Value must not contain special character`)
    : undefined;

export const isAlphanumeric = (value) =>
  isBlank(value) || (value && alphanumericRegEx.test(value))
    ? undefined
    : i18n._(t`Value must be alphanumeric`);

export const isNumeric = (value) =>
  isBlank(value) || (value && numberRegEx.test(value))
    ? undefined
    : i18n._(t`Value must be numeric`);

export const isEmail = (value) => {
  return isBlank(value) || (value && emailRegEx.test(value))
    ? undefined
    : i18n._(t`Value must be a valid email`);
};

export const isUrl = (value) => {
  urlRegEx.lastIndex = 0;
  return isBlank(value) || (value && urlRegEx.test(value))
    ? undefined
    : i18n._(t`Value must be a valid URL`);
};

export const contains = (container, value, fieldName, type) => {
  if (type === "Checklist" && Array.isArray(container)) {
    container = formatChecklist(container);
  }
  return !value || !container || container.includes(value)
    ? undefined
    : i18n._(t`Value does not contain ${value}`);
};

export const notContains = (container, value, fieldName, type) => {
  if (type === "Checklist" && Array.isArray(container)) {
    container = formatChecklist(container);
  }
  return !value || !container || !container.includes(value)
    ? undefined
    : i18n._(t`Value should not contain ${value}`);
};

export const isPartOf = (value, container, fieldName, type) => {
  if (type === "User") {
    container =
      container &&
      container.map((item) => {
        return item._id;
      });
    value = value && value._id;
  } else if (type === "MultiUser") {
    container =
      container &&
      container.map((item) => {
        return item._id;
      });
    value =
      value &&
      value.map((item) => {
        return item._id;
      });
    if (container && value && container.length >= value.length) {
      let isPartOf = true;
      for (let i = 0; i < value.length; i++) {
        if (!container.includes(value[i])) {
          isPartOf = false;
          break;
        }
      }
      return !isPartOf;
    }
    return true;
  }
  return isBlank(value) || (!isBlank(container) && container.includes(value))
    ? undefined
    : i18n._(t`Value is not part of ${container}`);
};

export const isNotPartOf = (value, container, fieldName, type) => {
  if (type === "User") {
    container =
      container &&
      container.map((item) => {
        return item._id;
      });
    value = value && value._id;
  } else if (type === "MultiUser") {
    container =
      container &&
      container.map((item) => {
        return item._id;
      });
    value =
      value &&
      value.map((item) => {
        return item._id;
      });
    if (container && value && container.length >= value.length) {
      let isNotPartOf = true;
      for (let i = 0; i < value.length; i++) {
        if (container.includes(value[i])) {
          isNotPartOf = false;
        } else {
          isNotPartOf = true;
          break;
        }
      }
      return !isNotPartOf;
    }
    return undefined;
  }
  return isBlank(value) ||
    isBlank(container) ||
    (container && !container.includes(value))
    ? undefined
    : i18n._(t`Value should not be a part of ${container}`);
};

export const lessThan = (LHS, RHS, fieldName, type) => {
  let displayValue = getDisplayValue(RHS, fieldName, type);
  return isBlank(LHS) || isBlank(RHS) || LHS < RHS
    ? undefined
    : i18n._(t`Value must be less than ${displayValue}`);
};

export const lessThanEqualTo = (LHS, RHS, fieldName, type) => {
  let displayValue = getDisplayValue(RHS, fieldName, type);
  return isBlank(LHS) || isBlank(RHS) || LHS <= RHS
    ? undefined
    : i18n._(t`Value must be less than or equal to ${displayValue}`);
};

export const greaterThan = (LHS, RHS, fieldName, type) => {
  let displayValue = getDisplayValue(RHS, fieldName, type);
  return isBlank(LHS) || isBlank(RHS) || LHS > RHS
    ? undefined
    : i18n._(t`Value must be greater than ${displayValue}`);
};

export const greaterThanEqualTo = (LHS, RHS, fieldName, type) => {
  let displayValue = getDisplayValue(RHS, fieldName, type);
  return isBlank(LHS) || isBlank(RHS) || LHS >= RHS
    ? undefined
    : i18n._(t`Value must be greater than or equal to ${displayValue}`);
};

export const equalTo = (LHS, RHS, fieldName, fieldType) => {
  if (fieldType === "Date" || fieldType === "DateTime") {
    let displayValue = getDisplayValue(RHS, fieldName, fieldType);
    return isBlank(LHS) ||
      isBlank(RHS) ||
      (LHS && RHS && LHS.getTime() === RHS.getTime())
      ? undefined
      : i18n._(t`Value must be equal to ${displayValue}`);
  } else if (fieldType === "Boolean") {
    let value = "";
    let RHSValue = RHS;
    LHS = !!LHS;
    if (RHS && typeof RHS === "string") {
      RHSValue = RHSValue.toLowerCase();
    }
    if (["1", "true", "yes", 1, true].includes(RHSValue)) {
      value = true;
    } else {
      value = false;
    }
    return LHS === value
      ? undefined
      : i18n._(t`Value must be equal to ${fieldName || RHS}`);
  } else if (fieldType === "User" || fieldType === "UserAndGroup") {
    let displayValue = getDisplayValue(RHS, fieldName, fieldType);
    return isBlank(LHS) || (LHS && RHS && LHS._id === RHS._id)
      ? undefined
      : i18n._(t`Value must be equal to ${displayValue}`);
  } else if (fieldType === "MultiUser") {
    return undefined;
  } else {
    return isBlank(LHS) || LHS === RHS
      ? undefined
      : i18n._(t`Value must be equal to ${fieldName || RHS}`);
  }
};
export const notEqualTo = (LHS, RHS, fieldName, fieldType) => {
  if (fieldType === "Date" || fieldType === "DateTime") {
    let displayValue = getDisplayValue(RHS, fieldName, fieldType);
    return isBlank(LHS) ||
      isBlank(RHS) ||
      (LHS && RHS && LHS.getTime() !== RHS.getTime())
      ? undefined
      : i18n._(t`Value must not be equal to ${displayValue}`);
  } else if (fieldType === "Boolean") {
    let value = "";
    let RHSValue = RHS;
    LHS = !!LHS;
    if (RHS && typeof RHS === "string") {
      RHSValue = RHSValue.toLowerCase();
    }
    if (["1", "true", "yes", 1, true].includes(RHSValue)) {
      value = true;
    } else {
      value = false;
    }
    return LHS !== value
      ? undefined
      : i18n._(t`Value must not be  ${fieldName || RHS}`);
  } else if (fieldType === "User" || fieldType === "UserAndGroup") {
    let displayValue = getDisplayValue(RHS, fieldName, fieldType);
    return isBlank(LHS) || (LHS && RHS && LHS._id !== RHS._id)
      ? undefined
      : i18n._(t`Value must not be equal to ${displayValue}`);
  } else {
    return isBlank(LHS) || LHS !== RHS
      ? undefined
      : i18n._(t`Value must not be equal to ${fieldName || RHS}`);
  }
};

export const isInTheFuture = (LHS, RHS, fieldName) =>
  isBlank(LHS) || LHS >= new Date()
    ? undefined
    : i18n._(t`Value must be in the future`);

export const isInThePast = (LHS, RHS, fieldName, type) => {
  let RHSValue = new Date();
  if (type === "Date") {
    RHSValue = new Date(
      RHSValue.getFullYear(),
      RHSValue.getMonth(),
      RHSValue.getDate()
    );
    LHS = LHS && new Date(LHS.getFullYear(), LHS.getMonth(), LHS.getDate());
  }
  return isBlank(LHS) || LHS < RHSValue
    ? undefined
    : i18n._(t`Value must be in the past`);
};

export const isNotInThePast = (LHS, RHS, fieldName, type) => {
  let RHSValue = new Date();
  if (type === "Date") {
    RHSValue = new Date(
      RHSValue.getFullYear(),
      RHSValue.getMonth(),
      RHSValue.getDate()
    );
    LHS = LHS && new Date(LHS.getFullYear(), LHS.getMonth(), LHS.getDate());
  } else {
    let seconds = RHSValue.getSeconds();
    let milliSeconds = RHSValue.getMilliseconds();
    LHS && LHS.setSeconds(seconds);
    LHS && LHS.setMilliseconds(milliSeconds);
  }
  return isBlank(LHS) || LHS >= RHSValue
    ? undefined
    : i18n._(t`Value must not be in the past`);
};

export const isYes = (value) =>
  isBlank(value) || value ? undefined : i18n._(t`Value must be Yes`);
const isNo = (value) =>
  isBlank(value) || !value ? undefined : i18n._(t`Value must be No`);

const AND = (LHS, RHS) => !!LHS && !!RHS;
const OR = (LHS, RHS) => !!LHS || !!RHS;

export const maxSelection = (LHS, RHS, fieldName, type) => {
  if (type === "Checklist" && Array.isArray(LHS)) {
    LHS = formatChecklist(LHS);
  }
  return !isBlank(LHS) && LHS.length > parseInt(RHS, 10)
    ? i18n._(
        t`Number of selections should not be more than ${fieldName || RHS}`
      )
    : undefined;
};

const minSelection = (LHS, RHS, fieldName, type) => {
  if (type === "Checklist" && Array.isArray(LHS)) {
    LHS = formatChecklist(LHS);
  }
  return isBlank(LHS) || LHS.length >= parseInt(RHS, 10)
    ? undefined
    : i18n._(t`Number of selection should be at least ${fieldName || RHS}`);
};

const selectAll = (LHS, RHS, fieldName, type) => {
  let filteredLHS = [];
  if (type === "Checklist" && Array.isArray(LHS)) {
    filteredLHS = formatChecklist(LHS);
  }
  return filteredLHS.isEmpty() ||
    (!filteredLHS.isEmpty() &&
      !LHS.isEmpty() &&
      filteredLHS.length === LHS.length)
    ? undefined
    : i18n._(t`All items need to be selected`);
};

const selectionShouldContain = (LHS, RHS, fieldName) => {
  return isBlank(LHS) || (LHS && LHS.includes(RHS))
    ? undefined
    : i18n._(t`Selection should contain ${fieldName || RHS}`);
};

const selectionShouldNotContain = (LHS, RHS, fieldName) => {
  return LHS && LHS.includes(RHS)
    ? i18n._(t`Selection should not contain ${fieldName || RHS}`)
    : undefined;
};

const attachmentShouldNotExceed = (LHS, RHS, fieldName) => {
  return undefined;
};

const attachmentType = (LHS, RHS, fieldName) => {
  return undefined;
};

const maxNumberOfAttachments = (LHS, RHS, fieldName) => {
  if (RHS && Number(RHS) && LHS && LHS.length > Number(RHS)) {
    return i18n._(t`Should not exceed ${RHS} number of attachments`);
  }
  return undefined;
};

const minNumberOfAttachments = (LHS, RHS, fieldName) => {
  if (
    RHS &&
    Number(RHS) &&
    ((!isBlank(LHS) && LHS.length < Number(RHS)) || isBlank(LHS))
  ) {
    return i18n._(t`Attach at least ${RHS} number of attachments`);
  }
  return undefined;
};

const countEqualTo = (LHS, RHS, fieldName, type) => {
  if (type === "Checklist") {
    LHS = formatChecklist(LHS);
    LHS = LHS.length;
  }
  if (!isNaN(RHS)) {
    RHS = parseInt(RHS, 10);
  }
  return LHS === RHS
    ? undefined
    : i18n._(t`Selected count must be equal to ${RHS}`);
};
const countNotEqualTo = (LHS, RHS, fieldName, type) => {
  if (type === "Checklist") {
    LHS = formatChecklist(LHS);
    LHS = LHS.length;
  }
  if (!isNaN(RHS)) {
    RHS = parseInt(RHS, 10);
  }
  return LHS !== RHS
    ? undefined
    : i18n._(t`Selected count must not be equal to ${RHS}`);
};
const countLessThan = (LHS, RHS, fieldName, type) => {
  if (type === "Checklist") {
    LHS = formatChecklist(LHS);
  }
  if (Array.isArray(LHS)) {
    LHS = LHS.length;
  }
  if (!isNaN(RHS)) {
    RHS = parseInt(RHS, 10);
  }
  return LHS > RHS
    ? undefined
    : i18n._(t`Selected count must be less than ${RHS}`);
};
const countLessThanEqualTo = (LHS, RHS, fieldName, type) => {
  if (type === "Checklist") {
    LHS = formatChecklist(LHS);
  }
  if (Array.isArray(LHS)) {
    LHS = LHS.length;
  }
  if (!isNaN(RHS)) {
    RHS = parseInt(RHS, 10);
  }
  return LHS <= RHS
    ? undefined
    : i18n._(t`Selected count must be less than or equal to ${RHS}`);
};
const countGreaterThan = (LHS, RHS, fieldName, type) => {
  if (type === "Checklist") {
    LHS = formatChecklist(LHS);
  }
  if (Array.isArray(LHS)) {
    LHS = LHS.length;
  }
  if (!isNaN(RHS)) {
    RHS = parseInt(RHS, 10);
  }
  return LHS > RHS
    ? undefined
    : i18n._(t`Selected count must be greater than ${RHS}`);
};
const countGreaterThanEqualTo = (LHS, RHS, fieldName, type) => {
  if (type === "Checklist") {
    LHS = formatChecklist(LHS);
  }
  if (Array.isArray(LHS)) {
    LHS = LHS.length;
  }
  if (!isNaN(RHS)) {
    RHS = parseInt(RHS, 10);
  }
  return LHS >= RHS
    ? undefined
    : i18n._(t`Selected count must be greater than or equal to ${RHS}`);
};

const domainEqualTo = (LHS, RHS, fieldName) => {
  if (!LHS) {
    return undefined;
  } else {
    let [emailName, emailDomain] = LHS.split("@");
    return emailName.length && emailDomain === RHS
      ? undefined
      : i18n._(t`Domain should match with ${fieldName || RHS}`);
  }
};

const domainNotEqualTo = (LHS, RHS, fieldName) => {
  if (!LHS) {
    return undefined;
  } else {
    let [emailName, emailDomain] = LHS.split("@");
    return emailName.length && emailDomain !== RHS
      ? undefined
      : i18n._(t`Domain should not match with ${fieldName || RHS}`);
  }
};

const startsWith = (LHS, RHS, fieldName) => {
  return !LHS || LHS.startsWith(RHS)
    ? undefined
    : i18n._(t`should start with ${fieldName || RHS}`);
};

const endsWith = (LHS, RHS, fieldName) => {
  return !LHS || LHS.endsWith(RHS)
    ? undefined
    : i18n._(t`should end with ${fieldName || RHS}`);
};

const customRegex = (LHS, RHS, FieldName, type) => {
  let regex = new RegExp(RHS);
  return !LHS || regex.test(LHS)
    ? ""
    : i18n._(t`Entered value does not match the format ${RHS}`);
};

let operatorMapping = {
  LESS_THAN: lessThan,
  LESS_THAN_OR_EQUAL_TO: lessThanEqualTo,
  EQUAL_TO: equalTo,
  NOT_EQUAL_TO: notEqualTo,
  GREATER_THAN: greaterThan,
  GREATER_THAN_OR_EQUAL_TO: greaterThanEqualTo,
  AND: AND,
  OR: OR,
  MAX_LENGTH: maxLength,
  MIN_LENGTH: minLength,
  CONTAINS: contains,
  NOT_CONTAINS: notContains,
  PART_OF: isPartOf,
  NOT_PART_OF: isNotPartOf,
  ALPHA_NUMERIC: isAlphanumeric,
  ALPHANUMERIC: isAlphanumeric,
  ALPHABETS: isAlphabet,
  NUMERIC: isNumeric,
  NO_SPECIAL_CHARACTER: noSpecialCharacters,
  EMAIL: isEmail,
  URL: isUrl,
  YES: isYes,
  NO: isNo,
  IN_THE_FUTURE: isInTheFuture,
  IN_THE_PAST: isInThePast,
  NOT_IN_THE_PAST: isNotInThePast,
  MAX_NO_OF_SELECTION: maxSelection,
  MIN_NO_OF_SELECTION: minSelection,
  SELECTION_SHOULD_CONTAIN: selectionShouldContain,
  SELECTION_SHOULD_NOT_CONTAIN: selectionShouldNotContain,
  ATTACHMENT_SIZE_IN_MEGA_BYTE: attachmentShouldNotExceed,
  ATTACHMENT_TYPE: attachmentType,
  MAX_NO_OF_ATTACHMENTS: maxNumberOfAttachments,
  MIN_NO_OF_ATTACHMENTS: minNumberOfAttachments,
  IMAGE_HEIGHT_IN_PIXEL: () => {},
  IMAGE_WIDTH_IN_PIXEL: () => {},
  VALUE_EQUAL_TO: equalTo,
  VALUE_NOT_EQUAL_TO: notEqualTo,
  VALUE_LESS_THAN: lessThan,
  VALUE_LESS_THAN_OR_EQUAL_TO: lessThanEqualTo,
  VALUE_GREATER_THAN: greaterThan,
  VALUE_GREATER_THAN_OR_EQUAL_TO: greaterThanEqualTo,
  COUNT_EQUAL_TO: countEqualTo,
  COUNT_NOT_EQUAL_TO: countNotEqualTo,
  COUNT_LESS_THAN: countLessThan,
  COUNT_LESS_THAN_OR_EQUAL_TO: countLessThanEqualTo,
  COUNT_GREATER_THAN: countGreaterThan,
  COUNT_GREATER_THAN_OR_EQUAL_TO: countGreaterThanEqualTo,
  TYPE_EQUAL_TO: () => {},
  TYPE_NOT_EQUAL_TO: () => {},
  SELECT_ALL: selectAll,
  EMPTY: isEmpty,
  NOT_EMPTY: isNotEmpty,
  DOMAIN_EQUAL_TO: domainEqualTo,
  DOMAIN_NOT_EQUAL_TO: domainNotEqualTo,
  STARTS_WITH: startsWith,
  ENDS_WITH: endsWith,
  REGEX: customRegex
};

const castValue = (value, type) => {
  if (type === FIELD_TYPES.DATE_TIME && value) {
    if (new Date(parseDate(value, true, true)) !== "Invalid Date") {
      value = parseDate(value, true, true);
    }
    return new Date(dateFromString(value));
  }
  if (type === FIELD_TYPES.DATE && value) {
    return new Date(value);
  }
  if (type === FIELD_TYPES.CURRENCY && value) {
    let iValue = value.split(" ");
    value = parseFloat(iValue.join(" "));
    return value;
  }
  if (
    value &&
    (type === FIELD_TYPES.NUMBER ||
      type === FIELD_TYPES.SLIDER ||
      type === FIELD_TYPES.STAR_RATING)
  ) {
    value = parseFloat(value);
  }
  if (type === FIELD_TYPES.TEXT_AREA) {
    return getTextFromMarkdown(value);
  }
  return value;
};

const constructCondition = (
  condition,
  dependentFields,
  addLHS,
  systemFields
) => {
  let operator = condition.getValue("Operator");
  let RHS = condition.getValue("RHS" + condition.getValue("RHSType"));
  let LHSField = condition.getValue("LHSOwnField");
  let isAppearance = false;
  if (!LHSField) {
    let criteriaId = condition.getValue("Criteria");
    if (criteriaId) {
      let criteriaModel = condition.getModel(criteriaId);
      LHSField = criteriaModel.getValue("FieldValidation");
    } else if (condition.getValue("Appearance")) {
      isAppearance = true;
      let appearanceModel = condition.getModel(
        condition.getValue("Appearance")
      );
      let columnId = appearanceModel.getValue("Column");
      let columnModel = condition.getModel(columnId);
      LHSField = columnModel.getList("Column", "Field")[0].getValue("Id");
    }
  }
  let errorMessage = condition.getValue("ErrorMessage");
  let LHS =
    LHSField &&
    condition.getModel(LHSField) &&
    condition.getModel(LHSField).getValue("Id");
  let lhsFieldType =
    LHSField &&
    condition.getModel(LHSField) &&
    condition.getModel(LHSField).getValue("Type");
  if (!LHS) {
    let matchedField;
    matchedField =
      systemFields &&
      systemFields.filter((field) => {
        return field.Id === LHSField;
      });
    if (matchedField && !matchedField.isEmpty()) {
      LHS = matchedField[0]["Id"];
      lhsFieldType = matchedField[0]["Type"];
    }
  }
  if (condition.getValue("RHSType") === "Field") {
    let rhsFieldType, RHSName;
    if (condition.getModel(RHS)) {
      rhsFieldType =
        condition.getModel(RHS) && condition.getModel(RHS).getValue("Type");
      RHSName =
        condition.getModel(RHS) && condition.getModel(RHS).getValue("Name");
      RHS = condition.getModel(RHS) && condition.getModel(RHS).getValue("Id");
      let RHSColumnId =
        condition.getModel(RHS) && condition.getModel(RHS).getValue("Column");
      dependentFields = [
        ...dependentFields,
        { fieldId: RHS, columnId: RHSColumnId }
      ];
    } else {
      let matchedField;
      matchedField =
        systemFields &&
        systemFields.filter((field) => {
          return field.Id === RHS;
        });
      if (matchedField && !matchedField.isEmpty()) {
        RHS = matchedField[0]["Id"];
        RHSName = matchedField[0]["Name"];
        rhsFieldType = matchedField[0]["Type"];
      }
    }
    return [
      (allValues, isVisibility, formData, systemFields) => {
        try {
          // need to remove and check once fixed
          let lhsValue = castValue(
            allValues && allValues.getValue(LHS),
            lhsFieldType || rhsFieldType
          );
          let rhsValue = castValue(
            allValues && allValues.getValue(RHS),
            rhsFieldType
          );
          // let matchedField;
          // matchedField =
          //   systemFields &&
          //   systemFields.filter((field) => {
          //     return field.Id === RHS;
          //   });
          // if (
          //   matchedField &&
          //   !matchedField.isEmpty() &&
          //   (!rhsValue || rhsValue === "") &&
          //   formData
          // ) {
          //   rhsValue = castValue(formData.getValue(RHS), rhsFieldType);
          // }
          let result =
            operatorMapping[operator] &&
            operatorMapping[operator](
              lhsValue,
              rhsValue,
              RHSName,
              lhsFieldType
            );
          if (
            (isAppearance || isVisibility) &&
            isBlank(lhsValue) &&
            operator !== "EMPTY" &&
            operator !== "NOT_EMPTY"
          ) {
            result = "Appearance is blank";
          }
          if (result) {
            if (addLHS) {
              result = `${
                LHSField && condition.getModel(LHSField).getValue("Name")
              }: ${result}`;
            }
            return errorMessage || result;
          }
          return undefined;
        } catch (e) {
          console.error("Couldnt evaluate condition", e);
          return undefined;
        }
      },
      dependentFields
    ];
  }
  return [
    (allValues, isVisibility) => {
      // need to remove and check once fixed
      let lhsValue = castValue(
        allValues && allValues.getValue(LHS),
        lhsFieldType
      );
      let rhsValue = castValue(RHS, lhsFieldType);
      let result =
        operatorMapping[operator] &&
        operatorMapping[operator](lhsValue, rhsValue, null, lhsFieldType);
      if (
        (isAppearance || isVisibility) &&
        isBlank(lhsValue) &&
        operator !== "EMPTY" &&
        operator !== "NOT_EMPTY"
      ) {
        return "Appearance is blank";
      }
      if (result) {
        if (addLHS) {
          result = `${
            LHSField && condition.getModel(LHSField).getValue("Name")
          }: ${result}`;
        }
        return errorMessage || result;
      }
      return undefined;
    },
    dependentFields
  ];
};

export const constructCriteria = (
  criteriaList,
  dependentFields,
  addLHS,
  combineResults,
  systemFields
) => {
  let criteriaExpressionList = criteriaList.map((criteria) => {
    let isOR = criteria.getValue("IsOR");
    let conditionExpressionList = [];
    try {
      conditionExpressionList = criteria
        .getList("Criteria", "Condition")
        .map((condition) => {
          let result = constructCondition(
            condition,
            dependentFields,
            addLHS,
            systemFields
          );
          dependentFields = result[1];
          return result[0];
        });
    } catch (e) {
      console.error("Couldn't construct condition", e);
    }

    let criteriaExpression = (allValues, formData) => {
      let conditionResult =
        conditionExpressionList &&
        conditionExpressionList.map((condition) => {
          return condition(
            allValues,
            Object.keys(criteria.currentJson).includes("ColumnVisibility"),
            formData,
            systemFields
          );
        });
      conditionResult = conditionResult.filter((result) => {
        return result !== undefined;
      });
      if (
        conditionResult &&
        conditionExpressionList &&
        conditionResult.length < conditionExpressionList.length
      ) {
        if (isOR || (!isOR && conditionResult.length === 0)) {
          if (Object.keys(criteria.currentJson).includes("ColumnVisibility")) {
            return criteria.getValue("IsHidden") ? "Hidden" : "Visible";
          }
          return undefined;
        }
      }
      return combineResults ? conditionResult : conditionResult[0];
    };

    return criteriaExpression;
  });
  return [criteriaExpressionList, dependentFields];
};

export const validationGenerator = (field, dependentFields, systemFields) => {
  let validationList = [];
  let validationExpression;
  if (field.getValue("Required")) {
    let requiredFunc = (formData) => {
      let result = required(
        formData.getValue(field.getValue("Id")),
        field.getValue("Type")
      );
      if (result) {
        return [result];
      }
      return result;
    };
    validationList = [...validationList, requiredFunc];
  }
  if (field.getValue("Type") === "Email") {
    let emailVatidationFunc = (formData) => {
      let result = isEmail(formData.getValue(field.getValue("Id")));
      if (result) {
        return [result];
      }
      return result;
    };
    validationList = [...validationList, emailVatidationFunc];
  }
  if (field.getList("FieldValidation", "Criteria").length !== 0) {
    [validationExpression, dependentFields] = constructCriteria(
      field.getList("FieldValidation", "Criteria"),
      dependentFields,
      null,
      true,
      systemFields
    );
    let validationFunction = (allValues, formData) => {
      let result =
        validationExpression[0] && validationExpression[0](allValues, formData);
      if (result) {
        return result;
      }
      return undefined;
    };
    validationList = [...validationList, validationFunction];
  }

  return [validationList, dependentFields];
};

export const validationCheckKey = (field, dependentFields) => {
  let validationList = [];
  if (field["Required"]) {
    let requiredFunc = (formData) => {
      let result = required(formData.getValue(field["Id"]));
      if (result) {
        return [result];
      }
      return result;
    };
    validationList = [...validationList, requiredFunc];
  }
  return [validationList, dependentFields];
};

export const permissionGenerator = (
  column,
  sectionPermission,
  isStartStep,
  currentStepId,
  status,
  isSendBackToInitiator
) => {
  let permissionObject = {
    ColumnPermission: isStartStep ? "Editable" : "ReadOnly"
  };
  if (sectionPermission) {
    permissionObject["ColumnPermission"] = sectionPermission;
    if (sectionPermission === "Hidden") {
      return { ColumnPermission: "Hidden" };
    }
  }
  let columnPermission = column
    .getList("Column", "Permission")
    .filter((permission) => permission.getValue("Activity") === currentStepId);
  if (columnPermission.length > 0) {
    permissionObject["ColumnPermission"] =
      columnPermission[0].getValue("Permission");
  }
  if (
    (!isStartStep &&
      status === "Completed" &&
      permissionObject["ColumnPermission"] !== "Hidden") ||
    (isStartStep &&
      status &&
      status !== "Draft" &&
      !isSendBackToInitiator &&
      permissionObject["ColumnPermission"] === "Editable" &&
      currentStepId)
  ) {
    permissionObject["ColumnPermission"] = "ReadOnly";
  }
  return permissionObject;
};

export const appearanceGenerator = (
  column,
  dependentFields,
  currentStep,
  systemFields
) => {
  let color = column.getValue("Color");
  let fontSize = column.getValue("FontSize");
  let activityAppearances = column.getList("ActivityColumn", "Appearance");
  let activityAppearance = activityAppearances.filter((activity) => {
    return activity.getValue("Activity") === currentStep;
  });
  if (activityAppearance[0]) {
    color = activityAppearance[0].getValue("Color");
    fontSize = activityAppearance[0].getValue("FontSize");
  }
  let appearanceList = column.getList("Column", "Appearance");
  let appearanceCondition = appearanceList.map((appearance) => {
    let condition = appearance
      .getList("Appearance", "Condition")
      .map((condition) => {
        let result = constructCondition(
          condition,
          dependentFields,
          systemFields
        );
        dependentFields = result[1];
        return result[0];
      });
    return {
      condition: condition[0],
      color: appearance.getValue("Color"),
      fontSize: appearance.getValue("FontSize")
    };
  });
  appearanceCondition.unshift({
    color,
    fontSize,
    condition: () => {
      return false;
    }
  });
  return [appearanceCondition, dependentFields];
};

export const visibilityGenerator = (column, systemFields) => {
  if (!column) {
    return;
  }
  let visibilityCriterias = constructCriteria(
    column.getList("ColumnVisibility", "Criteria"),
    [],
    null,
    false,
    systemFields
  );
  return visibilityCriterias[0];
};
